import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  ExternalLink, 
  FileText, 
  Info,
  Maximize, 
  Minimize,
  Play, 
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r-md">
      <div className="flex">
        <CheckCircle2 className="h-6 w-6 text-blue-500 flex-shrink-0 mr-3 mt-1" />
        <div className="text-blue-800">{children}</div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef} 
      className={`relative overflow-hidden rounded-lg border ${isFullscreen ? 'bg-white flex flex-col h-screen' : 'bg-gray-50'}`}
    >
      {/* Slide content */}
      <div className="relative flex-grow">
        <div className={`p-6 ${isFullscreen ? 'h-[calc(100vh-70px)] overflow-y-auto' : 'max-h-[70vh] overflow-y-auto'} thin-scrollbar`}>
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-medium text-gray-900">
              {slides[currentSlide].title}
            </h3>
            <div className="flex space-x-2">
              <button 
                onClick={toggleFullscreen}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
              
              {isFullscreen && (
                <button 
                  onClick={() => document.exitFullscreen()}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          
          <div className="prose max-w-none">
            {slides[currentSlide].content}
          </div>

          {slides[currentSlide].image && (
            <div className="flex justify-center mt-4">
              <img src={slides[currentSlide].image} alt="Slide image" className="max-w-full rounded" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation controls - now always visible in fullscreen too */}
      <div className={`flex items-center justify-between p-4 border-t bg-white ${isFullscreen ? 'mt-auto' : ''}`}>
        <div className="text-sm text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={nextSlide}
            aria-label="Next slide"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Video component with embedded Descript video
const VideoPlayer = () => {
  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden my-10">
      <iframe 
        src="https://share.descript.com/embed/BLEoLY2BoZw" 
        width="100%" 
        height="100%" 
        frameBorder="0" 
        allowFullScreen
        className="rounded-lg"
      ></iframe>
    </div>
  );
};

function CellularBasisOfHealth() {
  const [_, navigate] = useLocation();
  
  
  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Why Cellular Medicine?",
      content: (
        <>
          <p>Cellular medicine focuses on understanding and targeting the fundamental processes of a cell—its organelles, signaling pathways, and metabolic networks—to improve health outcomes. Traditional approaches often concentrate on organs or systems, but modern research shows that many diseases originate from or strongly involve cellular dysfunction.</p>
          
          <p>By comprehending the intracellular events that govern health and disease, clinicians gain powerful tools for diagnosing, preventing, and treating various conditions with greater precision. Recent advances in longevity science, functional medicine, and integrative approaches highlight the pivotal role of cellular processes such as:</p>
          
          <ul className="list-disc list-inside space-y-2 my-4 ml-4">
            <li>Senescence</li>
            <li>Oxidative and nitrosative stress</li>
            <li>Autophagy</li>
            <li>Apoptosis</li>
            <li>Metabolic homeostasis</li>
          </ul>
          
          <p>Understanding these processes at a basic science level aids clinicians in applying therapies, lifestyle interventions, and diagnostics that target problems "at the root," potentially offering more effective and personalized patient care.</p>
          
          <KeyTakeaway>
            Cellular medicine provides a framework for understanding disease at its most fundamental level, enabling more precise and personalized approaches to treatment.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 2,
      title: "Scope of This Lesson",
      content: (
        <>
          <p>This lesson introduces key concepts in cellular medicine that form the foundation for understanding health and disease at the cellular level:</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-3">Key Topics Covered</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>The foundational architecture of the cell—including key organelles</li>
              <li>Fundamental processes such as oxidative stress, nitrosative stress, autophagy, apoptosis, and senescence</li>
              <li>Metabolic pathways (e.g., hexosamine biosynthesis pathway, fatty acid oxidation, NAD metabolism)</li>
              <li>Key regulatory genes and molecules (e.g., p53, SIRT genes, microRNAs, cytokines, and chemokines)</li>
              <li>Clinical applications, including an overview of how understanding these processes can inform diagnostics and therapies</li>
            </ul>
          </div>
          
          <p>By the end of this lesson, you will have a comprehensive understanding of the cellular basis of health and disease, providing a foundation for applying cellular medicine principles in clinical practice.</p>
        </>
      )
    },
    {
      id: 3,
      title: "Basic Cell Structure and Function",
      content: (
        <>
          <p>To understand cellular medicine, one must start with the cell's foundational architecture. While there are many specialized cell types in the human body, each cell shares core features and organelles that are crucial to healthy function.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Plasma Membrane</h3>
              <p>A selectively permeable barrier composed primarily of phospholipids, cholesterol, and proteins. It houses receptor proteins that detect signals such as cytokines, hormones, and various extracellular ligands.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-indigo-800 mb-2">Cytoplasm and Cytoskeleton</h3>
              <p>The cytoplasm hosts numerous metabolic pathways, while the cytoskeleton provides structural support, facilitates cellular transport, and gives cells their shape.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Mitochondria</h3>
              <p>Often called the "powerhouse" of the cell, mitochondria are critical for ATP production via oxidative phosphorylation. They also coordinate ROS production, regulate apoptosis, and manage NAD/NADH balance.</p>
            </div>
            <div className="bg-pink-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-pink-800 mb-2">Nucleus</h3>
              <p>Contains most of a cell's genetic material in the form of chromatin. The arrangement and modifications of chromatin affect gene accessibility and transcription.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-amber-800 mb-2">Endoplasmic Reticulum and Golgi</h3>
              <p>The ER is where proteins are synthesized and lipids are metabolized. The Golgi apparatus further modifies proteins and lipids, packaging them for transport.</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-emerald-800 mb-2">Lysosomes and Autophagic Vesicles</h3>
              <p>Cellular "recycling" occurs in lysosomes, which degrade macromolecules. Autophagic vesicles form around damaged organelles or proteins, fusing with lysosomes to remove intracellular debris.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Understanding the structure and function of these cellular components is essential for recognizing how disruptions in their normal operation can lead to disease states.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "Key Molecules and Regulators in Cellular Medicine",
      content: (
        <>
          <p>Cellular function is governed by a complex network of molecular regulators. These key molecules play critical roles in maintaining cellular health and responding to stressors:</p>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-blue-900 mb-4">Key Molecular Regulators</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">p53</h4>
                  <p className="text-gray-700">Among the most critical tumor suppressor genes, p53 regulates cell cycle arrest, DNA repair, and apoptosis. When cells experience DNA damage, p53 is activated, halting the cell cycle to allow repairs or inducing apoptosis if damage is irreparable.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">SIRT Genes</h4>
                  <p className="text-gray-700">Sirtuins are a family of genes (SIRT1–SIRT7 in humans) linked to deacetylation processes affecting metabolism, DNA repair, and aging. SIRT gene transcription relies heavily on adequate NAD availability.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">MicroRNAs</h4>
                  <p className="text-gray-700">Non-coding RNAs such as MI146A fine-tune gene expression by silencing or modulating target mRNAs. MI146A has documented roles in regulating immune and inflammatory responses.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Cytokines and Chemokines</h4>
                  <p className="text-gray-700">Signaling proteins pivotal in immunity and inflammation. Overproduction or misregulation can fuel a persistent pro-inflammatory state associated with various diseases.</p>
                </div>
              </div>
            </div>
          </div>
          
          <p>Other important molecular regulators include BH4 (Tetrahydrobiopterin), which is necessary for neurotransmitter synthesis and nitric oxide production, and GLCNAC (N-Acetylglucosamine), which arises from the hexosamine biosynthesis pathway and modifies proteins via glycosylation.</p>
          
          <KeyTakeaway>
            Dysregulation of these molecular regulators can contribute to a wide range of pathologies, from cancer to metabolic disorders to neurodegenerative diseases.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 5,
      title: "Essential Cellular Processes",
      content: (
        <>
          <p>Several critical cellular processes maintain cellular homeostasis and respond to internal and external stressors. Disruption of these processes can lead to cellular dysfunction and disease:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-red-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-red-800 mb-2">Oxidative Stress</h3>
              <p>Arises from an imbalance between reactive oxygen species (ROS) and the cellular antioxidant defense system. Excessive ROS damage lipids, proteins, and DNA, leading to cellular dysfunction.</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-orange-800 mb-2">Nitrosative Stress</h3>
              <p>Related to oxidative stress but centers on nitrogen-based reactive species. When nitric oxide synthase becomes "uncoupled," it may produce superoxide instead of nitric oxide, amplifying oxidative damage.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-green-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-green-800 mb-2">Autophagy</h3>
              <p>A critical "self-eating" process wherein cells degrade damaged organelles and proteins. Active autophagy prevents the accumulation of dysfunctional cellular components.</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Apoptosis</h3>
              <p>Programmed cell death enables cells to remove themselves when they are no longer healthy or needed. A precise, orderly process that, unlike necrosis, is generally non-inflammatory.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Senescence</h3>
              <p>A state where cells stop dividing but remain metabolically active. Senescent cells can secrete pro-inflammatory factors, fueling local or systemic inflammation.</p>
            </div>
            <div className="bg-pink-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-pink-800 mb-2">Protein Glycation and Misfolding</h3>
              <p>Glycation occurs when sugars bind non-enzymatically to proteins, altering their function. Misfolded proteins cannot perform normal cellular tasks and may form aggregates linked to neurodegenerative diseases.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The balance of these cellular processes is essential for maintaining cellular health. Therapeutic interventions that target these processes may help restore normal cellular function in various disease states.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "Metabolic Pathways and Their Relevance",
      content: (
        <>
          <p>Cellular metabolism encompasses numerous interconnected pathways that generate energy, synthesize macromolecules, and maintain redox balance. Understanding these pathways provides insights into cellular health and disease:</p>
          
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-emerald-900 mb-4">Key Metabolic Pathways</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-emerald-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">NAD, NADPH, and Energy Metabolism</h4>
                  <p className="text-gray-700">NAD operates in redox reactions during processes like glycolysis and the Krebs cycle. NADPH supplies reducing power for biosynthesis and antioxidant defense. Maintaining an optimal NAD/NADPH ratio is central to redox homeostasis.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-emerald-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Hexosamine Biosynthesis Pathway (HBP)</h4>
                  <p className="text-gray-700">Converts glucose to intermediates like UDP-N-acetylglucosamine, integral for glycosylation of proteins. Overactivity in states of high glucose availability can drive pathological glycation.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-emerald-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Fatty Acid Oxidation</h4>
                  <p className="text-gray-700">Occurs mainly in mitochondria, producing acetyl-CoA that enters the Krebs cycle for ATP production. Impaired fatty acid oxidation leads to the accumulation of toxic intermediates that can promote inflammation.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-emerald-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Mitochondrial-Nuclear Interplay</h4>
                  <p className="text-gray-700">Communication between nucleus and mitochondria via retrograde and anterograde signaling coordinates energy demands with gene expression. Disruption can lead to persistent inflammation, senescence, or cell death.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Metabolic pathways are not just about energy production—they integrate with signaling networks to influence cellular fate, immune function, and stress responses. Therapeutic interventions that modulate these pathways may help restore cellular homeostasis in various disease states.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 7,
      title: "Examples of Clinical Application and Relevance",
      content: (
        <>
          <p>Understanding cellular processes and their dysregulation provides insights into the pathophysiology of various diseases and informs therapeutic approaches:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Neurodegenerative Diseases</h3>
              <p>Oxidative stress, misfolded proteins, and mitochondrial dysfunction are hallmarks of many neurodegenerative disorders. Therapies that bolster autophagy or reduce oxidative burden may slow disease progression.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Depression</h3>
              <p>Chronic systemic inflammation and oxidative/nitrosative stress have been implicated in depression pathophysiology. Interventions that restore normal NOS function or reduce pro-inflammatory cytokines could complement psychiatric management.</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-amber-800 mb-2">Metabolic Syndrome</h3>
              <p>Characterized by insulin resistance, inflammation, and mitochondrial dysfunction. Interventions that improve mitochondrial function, reduce oxidative stress, and enhance insulin sensitivity may help manage this condition.</p>
            </div>
          </div>
          
          <p>Other clinical applications include cancer therapy (targeting apoptosis pathways and DNA repair mechanisms), autoimmune disorders (modulating inflammatory responses), and aging-related conditions (addressing senescence and mitochondrial dysfunction).</p>
          
          <div className="bg-gray-100 p-5 rounded-md my-6">
            <h3 className="text-lg font-medium mb-3">Practical Clinical Applications</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Biomarker assessment to evaluate oxidative stress, mitochondrial function, and inflammatory status</li>
              <li>Therapeutic interventions targeting specific cellular pathways (e.g., NAD+ precursors, autophagy modulators)</li>
              <li>Lifestyle modifications to support cellular health (e.g., nutritional approaches, exercise, stress management)</li>
              <li>Personalized treatment protocols based on individual cellular function profiles</li>
            </ul>
          </div>
          
          <KeyTakeaway>
            Cellular medicine bridges the gap between basic science and clinical practice, offering a framework for understanding disease mechanisms and developing targeted therapeutic approaches. By addressing the cellular basis of health and disease, clinicians can provide more personalized and effective care.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 8,
      title: "Summary and Future Directions",
      content: (
        <>
          <p>Cellular medicine represents a paradigm shift in how we understand and approach health and disease:</p>
          
          <div className="bg-blue-50 p-5 rounded-md my-6">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Key Principles of Cellular Medicine</h3>
            <ul className="list-disc list-inside space-y-2 ml-2 text-blue-900">
              <li>Focus on cellular processes rather than organ systems alone</li>
              <li>Integration of molecular biology, biochemistry, and systems biology</li>
              <li>Understanding of how cellular dysfunction contributes to disease</li>
              <li>Development of therapies that target specific cellular pathways</li>
              <li>Personalization of treatment based on individual cellular function</li>
            </ul>
          </div>
          
          <p>As our understanding of cellular biology continues to evolve, so too will the field of cellular medicine. Future directions may include:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-indigo-800 mb-2">Advanced Diagnostic Tools</h3>
              <p>Development of more sensitive and specific biomarkers for assessing cellular function and dysfunction in various tissues.</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-emerald-800 mb-2">Targeted Therapies</h3>
              <p>Creation of interventions that precisely modulate specific cellular pathways to restore normal function or prevent disease progression.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-amber-800 mb-2">Integrative Approaches</h3>
              <p>Combination of cellular medicine with other disciplines to create comprehensive approaches to health and disease management.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-red-800 mb-2">Preventive Strategies</h3>
              <p>Development of preventive interventions based on understanding of cellular processes that precede clinical disease manifestation.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Cellular medicine offers a promising framework for advancing our understanding of health and disease and developing more effective therapeutic approaches. By focusing on the fundamental processes of cells, we can address the root causes of disease and promote optimal health at all levels.
          </KeyTakeaway>
        </>
      )
    }
  ];



  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-[#3498DB]"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
          
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Lecture Material
          </div>
        </div>
        
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
          The Cellular Basis of Health and Disease
        </h1>
        <p className="text-[#7F8C8D] mb-6">
          A comprehensive exploration of cellular processes and their impact on health and disease, 
          with insights into how these principles inform clinical practice.
        </p>
      
        <Slideshow slides={lectureSlides} />
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}

export default CellularBasisOfHealth;